import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Text } from '@repletoryreach/sdk-ui';
import { StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../../i18n/I18nProvider';

const icon = (children: ReactNode) => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
    {children}
  </svg>
);

const LockIcon = icon(<><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" /></>);
const ShieldIcon = icon(<><path d="M12 3l7 3v5.5c0 4.4-3 7.9-7 9.5-4-1.6-7-5.1-7-9.5V6z" /><path d="M9 12l2.2 2.2L15.5 10" /></>);
const RefreshIcon = icon(<><path d="M20 12a8 8 0 1 1-2.6-5.9" /><path d="M20 4v4.5h-4.5" /></>);

/**
 * What protects this account, and whether each protection is actually on.
 *
 * The reference design shows every row of this panel badged "Enforced". Only
 * the password policy is: the identity service rejects a password that misses
 * any of the five rules. Two-factor and recovery codes are per-account facts,
 * so those rows report this account's real state rather than a policy nobody
 * applies.
 */
export function SafeguardsPanel() {
  const t = useT();
  const { user } = useAuth();
  const twoFactorOn = user?.two_factor_enabled ?? false;

  // Same query key as the two-factor panel beside it, so the count is fetched
  // once and both cards agree on it.
  const status = useQuery({
    queryKey: ['admin', '2fa', 'status'],
    queryFn: () => identity.twoFactorStatus(),
    retry: false
  });
  const remaining = status.data?.data.recovery_codes_remaining;

  return (
    <Card withBorder>
      <Stack gap="md">
        <Stack gap="xs">
          <Title order={5}>{t('settings.security.safeguards.title')}</Title>
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('settings.security.safeguards.subtitle')}</Text>
        </Stack>

        <ul className="rr-security-safeguards">
          <Safeguard
            icon={LockIcon}
            title={t('settings.security.safeguards.password.title')}
            hint={t('settings.security.safeguards.password.hint')}
            state={<StatusBadge status="healthy">{t('settings.security.safeguards.password.state')}</StatusBadge>}
          />
          <Safeguard
            icon={ShieldIcon}
            title={t('settings.security.safeguards.twoFactor.title')}
            hint={twoFactorOn
              ? t('settings.security.safeguards.twoFactor.hintOn')
              : t('settings.security.safeguards.twoFactor.hintOff')}
            state={
              <StatusBadge status={twoFactorOn ? 'healthy' : 'warning'}>
                {twoFactorOn ? t('common.enabled') : t('settings.security.twoFactor.notEnrolled')}
              </StatusBadge>
            }
          />
          <Safeguard
            icon={RefreshIcon}
            title={t('settings.security.safeguards.recovery.title')}
            hint={twoFactorOn && typeof remaining === 'number'
              ? t('settings.security.safeguards.recovery.hintOn', { count: remaining })
              : t('settings.security.safeguards.recovery.hintOff')}
            /* A count the status call has not answered yet is not zero, and a
               zero here would read as "you have burned every recovery code". */
            state={twoFactorOn && typeof remaining === 'number'
              ? (
                <StatusBadge status={remaining <= 3 ? 'warning' : 'healthy'}>
                  {t('settings.security.safeguards.recovery.state', { count: remaining })}
                </StatusBadge>
              )
              : <StatusBadge status="inactive">{t('common.notAvailable')}</StatusBadge>}
          />
        </ul>
      </Stack>
    </Card>
  );
}

function Safeguard({ icon: tile, title, hint, state }: {
  icon: ReactNode;
  title: ReactNode;
  hint: ReactNode;
  state: ReactNode;
}) {
  return (
    <li>
      <div className="rr-security-safeguards__row">
        <span className="rr-security-safeguards__icon">{tile}</span>
        <Stack gap="xs">
          <Text role="bodySmall" fw={600}>{title}</Text>
          <Text role="caption" c="var(--rr-color-text-muted)">{hint}</Text>
        </Stack>
        {state}
      </div>
    </li>
  );
}
